/**
 * Hand-set order status for one work-order part row (#3568).
 * An empty value clears the override so purchase-order and stock rules apply again.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, MenuItem, TextField, Tooltip } from '@mui/material';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';
import { formatDate } from '@/utils/locale';
import {
  useUpdatePartOrderStatus,
  type PartOrderStatus,
} from '@/hooks/useWorkOrders';

/** Same shape as persistedId in billing/serviceDetailsConcurrency.ts (#3568). */
const PERSISTED_PART_ID = /^[0-9a-f]{8}-[0-9a-f-]{27}$/i;

const STATUS_OPTIONS = [
  { value: '', labelKey: 'partOrderStatus.automatic' },
  { value: 'ordered', labelKey: 'partOrderStatus.ordered' },
  { value: 'backordered', labelKey: 'partOrderStatus.backordered' },
  { value: 'arrived', labelKey: 'partOrderStatus.arrived' },
] as const;

export interface PartOrderStatusSelectProps {
  workOrderId: string;
  bookingServiceId: string;
  partId: string | undefined;
  value: PartOrderStatus | null;
  orderStatusSetAt?: string | null | undefined;
}

function toPartOrderStatus(raw: string): PartOrderStatus | null {
  if (raw === 'ordered' || raw === 'arrived' || raw === 'backordered') return raw;
  return null;
}

export function PartOrderStatusSelect({
  workOrderId,
  bookingServiceId,
  partId,
  value,
  orderStatusSetAt,
}: PartOrderStatusSelectProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const canUpdate = useAuthStore((s) => s.hasPermission('work_orders.update'));
  const updateOrderStatus = useUpdatePartOrderStatus();
  const requestSeq = useRef(0);
  // Optimistic choice until the refetched row matches it, or the row changes (#3568).
  const [pending, setPending] = useState<{
    partId: string;
    orderStatus: PartOrderStatus | null;
  } | null>(null);
  const pendingActive =
    pending !== null && pending.partId === partId && pending.orderStatus !== value;
  if (pending !== null && !pendingActive) setPending(null);
  const shown = pendingActive && pending ? pending.orderStatus : value;
  // The tooltip sits where the open menu draws its first option, Automaattinen,
  // and swallowed the click that resets the status. Hide it while open (#3582).
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const persisted = typeof partId === 'string' && PERSISTED_PART_ID.test(partId);
  const disabledReason = !persisted
    ? t('partOrderStatus.saveFirst')
    : !canUpdate
      ? t('partOrderStatus.noPermission')
      : '';
  const tooltip =
    disabledReason ||
    (orderStatusSetAt
      ? t('partOrderStatus.setByHand', { date: formatDate(orderStatusSetAt) })
      : '');
  const label = t('partOrderStatus.label');

  const handleChange = (raw: string) => {
    if (!persisted || !canUpdate || !partId) return;
    const next = toPartOrderStatus(raw);
    const seq = ++requestSeq.current;
    setPending({ partId, orderStatus: next });
    updateOrderStatus.mutate(
      { workOrderId, bookingServiceId, partId, orderStatus: next },
      {
        onError: (error) => {
          if (seq !== requestSeq.current) return;
          setPending(null);
          notify.error(
            tc('error'),
            describeApiError(error, tc, { fallback: t('partOrderStatus.updateFailed') }),
          );
        },
      },
    );
  };

  return (
    <Tooltip
      title={tooltip}
      open={tooltipOpen && !menuOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
    >
      <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
        <TextField
          select
          size="small"
          label={label}
          value={shown ?? ''}
          disabled={Boolean(disabledReason)}
          onChange={(event) => {
            const raw = event.target.value;
            handleChange(typeof raw === 'string' ? raw : '');
          }}
          sx={{ width: 150 }}
          slotProps={{
            inputLabel: { shrink: true },
            select: {
              displayEmpty: true,
              onOpen: () => setMenuOpen(true),
              onClose: () => setMenuOpen(false),
            },
            htmlInput: { 'aria-label': label },
          }}
        >
          {STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value || 'automatic'} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Tooltip>
  );
}
